class CTClickableBanner extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.slides = [...this.querySelectorAll('[data-ct-cb-slide]')];
    this.dots = [...this.querySelectorAll('[data-ct-cb-dot]')];
    this.previousButton = this.querySelector('[data-ct-cb-previous]');
    this.nextButton = this.querySelector('[data-ct-cb-next]');
    this.viewport = this.querySelector('[data-ct-cb-viewport]');

    this.index = Math.max(
      0,
      this.slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      )
    );

    this.autoplay = this.dataset.autoplay === 'true';
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.delay = Math.max(
      3000,
      Number(this.dataset.autoplaySpeed || 5) * 1000
    );

    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    this.touchStartX = null;
    this.preventClickUntil = 0;
    this.pointerInside = false;
    this.focusInside = false;
    this.abortController = new AbortController();

    const signal = this.abortController.signal;

    this.previousButton?.addEventListener(
      'click',
      () => this.show(this.index - 1, true),
      { signal }
    );

    this.nextButton?.addEventListener(
      'click',
      () => this.show(this.index + 1, true),
      { signal }
    );

    this.dots.forEach((dot) => {
      dot.addEventListener(
        'click',
        () => this.show(Number(dot.dataset.ctCbDot), true),
        { signal }
      );
    });

    this.viewport?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index - 1, true);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + 1, true);
        }
      },
      { signal }
    );

    this.viewport?.addEventListener(
      'touchstart',
      (event) => {
        this.touchStartX = event.touches[0]?.clientX ?? null;
      },
      { passive: true, signal }
    );

    this.viewport?.addEventListener(
      'touchend',
      (event) => {
        if (this.touchStartX === null) return;

        const touchEndX =
          event.changedTouches[0]?.clientX ?? this.touchStartX;

        const distance = touchEndX - this.touchStartX;
        this.touchStartX = null;

        if (Math.abs(distance) < 45) return;

        this.preventClickUntil = Date.now() + 400;
        this.show(this.index + (distance < 0 ? 1 : -1), true);
      },
      { passive: true, signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        const clickedBannerLink = event.target.closest(
          '[data-ct-cb-link]'
        );

        if (
          Date.now() <= this.preventClickUntil &&
          clickedBannerLink
        ) {
          event.preventDefault();
        }
      },
      { capture: true, signal }
    );

    if (this.pauseOnHover) {
      this.addEventListener(
        'mouseenter',
        () => {
          this.pointerInside = true;
          this.stop();
        },
        { signal }
      );

      this.addEventListener(
        'mouseleave',
        () => {
          this.pointerInside = false;
          this.start();
        },
        { signal }
      );

      this.addEventListener(
        'focusin',
        () => {
          this.focusInside = true;
          this.stop();
        },
        { signal }
      );

      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) {
            this.focusInside = false;
            this.start();
          }
        },
        { signal }
      );
    }

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this.stop();
          this.pauseSanta();
        } else {
          this.start();
          this.scheduleSanta(1000);
        }
      },
      { signal }
    );

    this.show(this.index, false);
    this.start();
    this.initCountdown();
    this.initSanta();
  }

  disconnectedCallback() {
    this.stop();
    this.pauseSanta();

    if (this.countdownTimer) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    this.abortController?.abort();
    this.initialized = false;
  }

  show(nextIndex, userInitiated = false) {
    if (this.slides.length === 0) return;

    this.index =
      (nextIndex + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;

      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));

      const link = slide.querySelector('[data-ct-cb-link]');

      if (link) {
        link.tabIndex = active ? 0 : -1;
      }
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.index;

      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', String(active));
    });

    if (userInitiated) {
      this.restart();
    }
  }

  start() {
    if (
      !this.autoplay ||
      this.reducedMotion ||
      this.slides.length < 2 ||
      document.hidden ||
      (
        this.pauseOnHover &&
        (this.pointerInside || this.focusInside)
      ) ||
      this.timer
    ) {
      return;
    }

    this.timer = window.setInterval(
      () => this.show(this.index + 1),
      this.delay
    );
  }

  stop() {
    if (!this.timer) return;

    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }

  initCountdown() {
    this.countdown = this.querySelector(
      '[data-ct-cb-countdown]'
    );

    if (!this.countdown) return;

    this.countdownFields = {
      days: this.countdown.querySelector(
        '[data-ct-cb-countdown-days]'
      ),
      hours: this.countdown.querySelector(
        '[data-ct-cb-countdown-hours]'
      ),
      minutes: this.countdown.querySelector(
        '[data-ct-cb-countdown-minutes]'
      )
    };

    this.countdownTimerElement = this.countdown.querySelector(
      '[data-ct-cb-countdown-timer]'
    );

    this.countdownComplete = this.countdown.querySelector(
      '[data-ct-cb-countdown-complete]'
    );

    this.updateCountdown();

    this.countdownTimer = window.setInterval(
      () => this.updateCountdown(),
      30000
    );
  }

  updateCountdown() {
    if (!this.countdown || !this.countdownFields) return;

    const now = new Date();
    const currentYear = now.getFullYear();

    const christmasStart = new Date(
      currentYear,
      11,
      25,
      0,
      0,
      0,
      0
    );

    const christmasEnd = new Date(
      currentYear,
      11,
      26,
      0,
      0,
      0,
      0
    );

    const isChristmasDay =
      now >= christmasStart && now < christmasEnd;

    if (isChristmasDay) {
      this.countdownTimerElement.hidden = true;
      this.countdownComplete.hidden = false;

      this.countdown.setAttribute(
        'aria-label',
        this.countdownComplete.textContent.trim()
      );

      return;
    }

    const targetYear =
      now >= christmasEnd ? currentYear + 1 : currentYear;

    const target = new Date(
      targetYear,
      11,
      25,
      0,
      0,
      0,
      0
    );

    const remaining = Math.max(
      0,
      target.getTime() - now.getTime()
    );

    const totalMinutes = Math.floor(remaining / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor(
      (totalMinutes % 1440) / 60
    );
    const minutes = totalMinutes % 60;

    this.countdownTimerElement.hidden = false;
    this.countdownComplete.hidden = true;

    this.countdownFields.days.textContent = String(
      days
    ).padStart(2, '0');

    this.countdownFields.hours.textContent = String(
      hours
    ).padStart(2, '0');

    this.countdownFields.minutes.textContent = String(
      minutes
    ).padStart(2, '0');

    this.countdown.setAttribute(
      'aria-label',
      `${days} days, ${hours} hours and ${minutes} minutes until Christmas`
    );
  }

  initSanta() {
    this.santaFlight = this.querySelector(
      '[data-ct-cb-santa]'
    );

    this.santaTrack = this.querySelector(
      '[data-ct-cb-santa-track]'
    );

    if (
      !this.santaFlight ||
      !this.santaTrack ||
      this.dataset.santaEnabled !== 'true' ||
      this.reducedMotion
    ) {
      return;
    }

    this.santaFirstDelay = Math.max(
      0,
      Number(this.dataset.santaFirstDelay || 2) * 1000
    );

    this.santaPauseDuration = Math.max(
      15000,
      Number(this.dataset.santaPauseDuration || 30) * 1000
    );

    this.santaTrack.addEventListener(
      'animationend',
      (event) => {
        if (event.target !== this.santaTrack) return;

        this.santaFlight.classList.remove('is-flying');
        this.scheduleSanta(this.santaPauseDuration);
      },
      { signal: this.abortController.signal }
    );

    const santaImage =
      this.santaFlight.querySelector('img');

    if (santaImage && !santaImage.complete) {
      santaImage.addEventListener(
        'load',
        () =>
          this.scheduleSanta(this.santaFirstDelay),
        {
          once: true,
          signal: this.abortController.signal
        }
      );

      return;
    }

    this.scheduleSanta(this.santaFirstDelay);
  }

  scheduleSanta(delay = 0) {
    if (
      !this.santaFlight ||
      this.reducedMotion ||
      document.hidden
    ) {
      return;
    }

    if (this.santaTimeout) {
      window.clearTimeout(this.santaTimeout);
    }

    this.santaTimeout = window.setTimeout(
      () => this.flySanta(),
      delay
    );
  }

  flySanta() {
    if (!this.santaFlight || document.hidden) return;

    this.santaFlight.classList.remove('is-flying');

    void this.santaFlight.offsetWidth;

    this.santaFlight.classList.add('is-flying');
  }

  pauseSanta() {
    if (this.santaTimeout) {
      window.clearTimeout(this.santaTimeout);
      this.santaTimeout = null;
    }

    this.santaFlight?.classList.remove('is-flying');
  }

  selectBlock(blockId) {
    const blockIndex = this.slides.findIndex(
      (slide) => slide.dataset.blockId === blockId
    );

    if (blockIndex < 0) return;

    this.stop();
    this.show(blockIndex, false);
  }
}

if (!customElements.get('ct-clickable-banner')) {
  customElements.define(
    'ct-clickable-banner',
    CTClickableBanner
  );
}

document.addEventListener(
  'shopify:block:select',
  (event) => {
    const banner = event.target.closest(
      'ct-clickable-banner'
    );

    banner?.selectBlock(event.detail.blockId);
  }
);

document.addEventListener(
  'shopify:block:deselect',
  (event) => {
    const banner = event.target.closest(
      'ct-clickable-banner'
    );

    banner?.start();
  }
);